import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'

import type { SalesChannel } from '@/api/admin-sales-channels-types'
import { useUpdateSalesChannel } from '@/api/api-mutations'
import {
	preloadListInventoryLocations,
	preloadListSalesChannelGroups,
	preloadListSalesChannelTypes,
	useListInventoryLocations,
	useListSalesChannelGroups
} from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

import { useSalesChannelTypeFieldOptions } from '../-sales-channel-type-utils'

export const Route = createFileRoute('/_authenticated/settings/sales-channels/$salesChannelUuid/edit')({
	component: RouteComponent,
	loader: async ({ context }) => {
		await preloadListSalesChannelTypes(context, {})
		await preloadListSalesChannelGroups(context, {})
		await preloadListInventoryLocations(context, { query: { limit: 100, offset: 0 } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Settings.SalesChannels.EditTitle()
	}
})

function RouteComponent() {
	const { salesChannelUuid } = Route.useParams()
	return (
		<RouteModal
			asDrawer
			requiresData
			loader={async api => await api.getSalesChannel({ path: { salesChannelUuid } })}
		>
			{data => <EditSalesChannelForm salesChannel={data} />}
		</RouteModal>
	)
}

function EditSalesChannelForm({ salesChannel }: { salesChannel: SalesChannel }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const { salesChannelUuid } = Route.useParams()
	const salesChannelTypeOptions = useSalesChannelTypeFieldOptions()
	const inventoryLocations = useListInventoryLocations({ limit: 100, offset: 0 })
	const inventoryLocationOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.SalesChannels.InventoryLocationNone() },
			...(inventoryLocations.data?.inventoryLocations ?? []).map(location => ({
				value: location.uuid,
				label: location.name
			}))
		],
		[L, inventoryLocations.data?.inventoryLocations]
	)
	const salesChannelGroups = useListSalesChannelGroups()
	const groupOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.SalesChannels.GroupNone() },
			...(salesChannelGroups.data ?? []).map(group => ({ value: group.uuid, label: group.name }))
		],
		[L, salesChannelGroups.data]
	)

	const updateSalesChannel = useUpdateSalesChannel()

	const form = useAppForm({
		defaultValues: {
			name: salesChannel.name,
			salesChannelTypeUuid: salesChannel.salesChannelType?.uuid ?? '',
			inventoryLocationUuid: salesChannel.inventoryLocation?.uuid ?? '',
			storefrontUrl: salesChannel.storefrontUrl ?? '',
			salesChannelGroupUuid: salesChannel.group?.uuid ?? ''
		},
		onSubmit: async ({ value }) => {
			const result = await updateSalesChannel({
				path: { salesChannelUuid },
				body: {
					name: value.name,
					salesChannelTypeUuid: value.salesChannelTypeUuid,
					inventoryLocationUuid: value.inventoryLocationUuid,
					storefrontUrl: value.storefrontUrl,
					salesChannelGroupUuid: value.salesChannelGroupUuid
				}
			})
			if (result.isSuccess) {
				toast.success(L.Settings.SalesChannels.EditSuccess())
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.SalesChannels.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => <field.TextField autoFocus label={L.Fields.Name()} />}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='salesChannelTypeUuid'
							children={field => (
								<field.SelectField
									label={L.Settings.SalesChannels.Column.SalesChannelType()}
									options={salesChannelTypeOptions}
								/>
							)}
						/>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Settings.SalesChannels.TypeOptionalHint()}
						</Text>
						<form.AppField
							name='inventoryLocationUuid'
							children={field => (
								<field.SelectField
									label={L.Settings.SalesChannels.Column.InventoryLocation()}
									options={inventoryLocationOptions}
									optionsLoading={inventoryLocations.isLoading}
								/>
							)}
						/>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Settings.SalesChannels.InventoryLocationOptionalHint()}
						</Text>
						<form.AppField
							name='salesChannelGroupUuid'
							children={field => (
								<field.SelectField
									label={L.Settings.SalesChannels.Column.Group()}
									options={groupOptions}
									optionsLoading={salesChannelGroups.isLoading}
								/>
							)}
						/>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Settings.SalesChannels.GroupOptionalHint()}
						</Text>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='storefrontUrl'
							children={field => (
								<field.TextField optional label={L.Settings.SalesChannels.StorefrontUrl()} />
							)}
						/>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Settings.SalesChannels.StorefrontUrlHint()}
						</Text>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
